// ajay:code 19-08-26
import { Plus, Trash2 } from "lucide-react";
import MultiLineList from "./MultiLineList";
import CauseRow from "./CauseRow";

const numInput = {
  width: 56,
  padding: "4px 6px",
  fontSize: 12,
  borderRadius: 6,
  border: "1px solid var(--border2)",
  background: "var(--surface2)",
  color: "var(--text)",
  textAlign: "center",
};

const emptyCause = () => ({
  cause_text: "",
  occurrence: "",
  control_prevention: [],
  control_detection: [],
  detection: "",
  recommended_action: "",
  responsibility: "",
  target_date: "",
  action_taken: [],
  revised_severity: "",
  revised_occurrence: "",
  revised_detection: "",
});

export default function FailureModeBlock({
  failureMode,
  onChange,
  onDelete,
  readOnly,
}) {
  const set = (field, value) => onChange({ ...failureMode, [field]: value });
  const causes = failureMode.causes || [];

  const updateCause = (i, updated) => {
    const next = [...causes];
    next[i] = updated;
    set("causes", next);
  };
  const addCause = () => set("causes", [...causes, emptyCause()]);
  const removeCause = (i) =>
    set(
      "causes",
      causes.filter((_, idx) => idx !== i),
    );

  return (
    <div
      style={{
        background: "rgba(0,194,255,0.04)",
        border: "1px solid var(--border2)",
        borderLeft: "3px solid var(--accent)",
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              color: "var(--accent)",
              fontWeight: 700,
              marginBottom: 3,
            }}
          >
            FAILURE MODE
          </div>
          {readOnly ? (
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {failureMode.failure_mode_text || "—"}
            </div>
          ) : (
            <input
              type="text"
              value={failureMode.failure_mode_text || ""}
              placeholder="e.g. CD-135.5±0.5mm out of spec"
              onChange={(e) => set("failure_mode_text", e.target.value)}
              style={{
                width: "100%",
                padding: "6px 8px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface2)",
                color: "var(--text)",
              }}
            />
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            title="Delete failure mode"
            onClick={onDelete}
            style={{
              background: "none",
              border: "none",
              color: "var(--danger)",
              cursor: "pointer",
              marginTop: 18,
            }}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>

      {/* Severity + Class */}
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "flex-end",
          marginTop: 8,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}>
            SEVERITY
          </div>
          <div style={{ fontSize: 9, color: "var(--text3)", marginBottom: 2 }}>
            1 (minor) – 10 (hazard)
          </div>
          {readOnly ? (
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              {failureMode.severity ?? "—"}
            </div>
          ) : (
            <input
              type="number"
              min={1}
              max={10}
              style={numInput}
              value={failureMode.severity ?? ""}
              onChange={(e) =>
                set("severity", e.target.value === "" ? "" : +e.target.value)
              }
            />
          )}
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}>
            CLASS
          </div>
          {readOnly ? (
            <div style={{ fontSize: 13 }}>{failureMode.class || "—"}</div>
          ) : (
            <input
              type="text"
              placeholder="e.g. SC"
              value={failureMode.class || ""}
              onChange={(e) => set("class", e.target.value)}
              style={{ ...numInput, width: 70, textAlign: "center" }}
            />
          )}
        </div>
      </div>

      <MultiLineList
        label="Potential Effect(s) of Failure"
        lines={failureMode.effects || []}
        onChange={(lines) => set("effects", lines)}
        readOnly={readOnly}
        placeholder="e.g. NP-Part Reject"
      />

      {/* Causes */}
      <div
        style={{
          marginTop: 10,
          paddingLeft: 10,
          borderLeft: "2px solid var(--border2)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: "var(--text3)",
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          CAUSES ({causes.length})
        </div>
        {causes.map((c, i) => (
          <CauseRow
            key={i}
            cause={c}
            severity={failureMode.severity}
            onChange={(updated) => updateCause(i, updated)}
            onDelete={() => removeCause(i)}
            readOnly={readOnly}
          />
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={addCause}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "var(--surface2)",
              border: "1px solid var(--border2)",
              borderRadius: 6,
              padding: "5px 10px",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--accent)",
              cursor: "pointer",
            }}
          >
            <Plus size={12} /> Add Cause
          </button>
        )}
      </div>
    </div>
  );
}
